'use client';

import { useCallback, useEffect, useMemo, useReducer, useState, type Dispatch } from 'react';
import { Sidebar } from '@/components/sidebar';
import { Header } from '@/components/header';
import { MetricsTimeRangeControls } from '@/components/metrics-time-range-controls';
import { MetricsRefreshControls, type MetricsViewActions } from '@/components/metrics-refresh-controls';
import { MetricsStorageUsage } from '@/components/metrics-storage-usage';
import { useMetrics } from './hooks/useMetrics';
import { TIME_RANGES, type TimeRangeKey, podVariant, LATENCY_THRESHOLDS } from './types';
import { FloatingToolbar } from './components/FloatingToolbar';
import { PasteGrafanaRangeButton } from './components/PasteGrafanaRangeButton';
import { ChartProvider, useChartContext } from './ChartContext';
import { AgreedLimitChart } from './components/AgreedLimitChart';
import { LatencyChart } from './components/LatencyChart';
import { InfraProbesChart } from './components/InfraProbesChart';
import { MetricsChart } from './components/MetricsChart';
import { TotalRequestsChart } from './components/TotalRequestsChart';
import { downsampleMetricsForDisplay } from './display-sampling';
import type { RateLimitMetricsSnapshot } from '@/lib/metrics-snapshot';

function ScopeBadge({ scope }: { scope: 'global' | 'pods' }) {
  const styles = scope === 'global'
    ? 'border-sky-500/40 bg-sky-500/10 text-sky-400'
    : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400';
  const label = scope === 'global' ? 'Global' : 'Pods';
  return (
    <span className={`inline-block rounded-sm border px-1.5 py-0 text-[9px] font-medium uppercase tracking-wide leading-relaxed ${styles}`}>
      {label}
    </span>
  );
}

function ChartCard({
  title,
  subtitle,
  scope,
  children,
  defaultOpen = true,
}: {
  title: string;
  subtitle?: string;
  scope?: 'global' | 'pods';
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
      className={`min-w-0 rounded-lg border bg-card shadow-sm ${open ? 'px-3 pt-3 pb-1' : 'px-3 py-2'}`}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 select-none">
        <span className={`inline-block transition-transform text-[10px] ${open ? 'rotate-90' : ''}`}>▶</span>
        <h2 className="text-sm font-semibold">{title}</h2>
        {scope && <ScopeBadge scope={scope} />}
        {subtitle && <span className="text-[11px] text-muted-foreground/70">{subtitle}</span>}
      </summary>
      {open && <div className="mt-2">{children}</div>}
    </details>
  );
}

function LatencyThresholdLegend() {
  return (
    <div className="px-1 mb-2 flex flex-wrap gap-4 text-[11px] text-muted-foreground">
      {LATENCY_THRESHOLDS.map((t) => (
        <span key={t.label} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-[2px] w-5 rounded-sm" style={{ background: t.color }} />
          {t.label} ({t.value}ms)
        </span>
      ))}
    </div>
  );
}

export type RangeState = {
  fromMs: number;
  toMs: number | null;
  liveTail: boolean;
  presetLabel: string | null;
  history: Array<{ fromMs: number; toMs: number | null; presetLabel: string | null }>;
  historyIndex: number;
};

export type RangeAction =
  | { type: 'setPreset'; key: TimeRangeKey; now?: number }
  | { type: 'loadSnapshot'; fromMs: number; toMs: number; presetLabel: string | null }
  | { type: 'setFromTo'; fromMs: number; toMs: number | null }
  | { type: 'zoom'; fromMs: number; toMs: number }
  | { type: 'setLiveTail'; value: boolean; now?: number }
  | { type: 'undo' }
  | { type: 'redo' };

const MAX_HISTORY = 50;

export function makeInitialRangeState(now: number): RangeState {
  const range = TIME_RANGES.find((r) => r.key === '15m')!;
  const fromMs = now - range.durationMs;
  const toMs = now;
  return {
    fromMs,
    toMs,
    liveTail: false,
    presetLabel: range.label,
    history: [{ fromMs, toMs, presetLabel: range.label }],
    historyIndex: 0,
  };
}

function navigateTo(
  state: RangeState,
  fromMs: number,
  toMs: number | null,
  liveTail: boolean,
): RangeState {
  const past = state.history.slice(0, state.historyIndex + 1);
  const next = [...past, { fromMs, toMs, presetLabel: null }];
  const trimmed = next.length > MAX_HISTORY ? next.slice(next.length - MAX_HISTORY) : next;
  return {
    fromMs,
    toMs,
    liveTail,
    presetLabel: null,
    history: trimmed,
    historyIndex: trimmed.length - 1,
  };
}

export function rangeReducer(state: RangeState, action: RangeAction): RangeState {
  switch (action.type) {
    case 'setPreset': {
      const now = action.now ?? Date.now();
      const range = TIME_RANGES.find((r) => r.key === action.key)!;
      const fromMs = now - range.durationMs;
      const toMs = now;
      return {
        fromMs,
        toMs,
        liveTail: false,
        presetLabel: range.label,
        history: [{ fromMs, toMs, presetLabel: range.label }],
        historyIndex: 0,
      };
    }
    case 'loadSnapshot':
      return {
        fromMs: action.fromMs,
        toMs: action.toMs,
        liveTail: false,
        presetLabel: action.presetLabel,
        history: [{
          fromMs: action.fromMs,
          toMs: action.toMs,
          presetLabel: action.presetLabel,
        }],
        historyIndex: 0,
      };
    case 'zoom':
      return navigateTo(state, action.fromMs, action.toMs, false);
    case 'setFromTo':
      return navigateTo(state, action.fromMs, action.toMs, false);
    case 'setLiveTail': {
      // Live tail is a mode toggle, not a navigation — intentionally not pushed to history.
      const now = action.now ?? Date.now();
      if (!action.value) return { ...state, liveTail: false, toMs: now };
      const previousTo = state.toMs ?? now;
      const duration = Math.max(0, previousTo - state.fromMs);
      return { ...state, fromMs: now - duration, liveTail: true, toMs: null };
    }
    case 'undo': {
      if (state.historyIndex <= 0) return state;
      const idx = state.historyIndex - 1;
      const entry = state.history[idx];
      return { ...state, fromMs: entry.fromMs, toMs: entry.toMs, presetLabel: entry.presetLabel, historyIndex: idx, liveTail: false };
    }
    case 'redo': {
      if (state.historyIndex >= state.history.length - 1) return state;
      const idx = state.historyIndex + 1;
      const entry = state.history[idx];
      return { ...state, fromMs: entry.fromMs, toMs: entry.toMs, presetLabel: entry.presetLabel, historyIndex: idx, liveTail: false };
    }
    default:
      return state;
  }
}

export function RateLimitMetricsContent({
  fromMs,
  toMs,
  liveTail,
  presetLabel,
  canUndo,
  canRedo,
  dispatchRange,
  showHeader = true,
  onActionsChange,
  onSnapshotChange,
  snapshot = null,
}: {
  fromMs: number;
  toMs: number | null;
  liveTail: boolean;
  presetLabel: string | null;
  canUndo: boolean;
  canRedo: boolean;
  dispatchRange: Dispatch<RangeAction>;
  showHeader?: boolean;
  onActionsChange?: (actions: MetricsViewActions | null) => void;
  onSnapshotChange?: (snapshot: RateLimitMetricsSnapshot | null) => void;
  snapshot?: RateLimitMetricsSnapshot | null;
}) {
  const [selectedVariants, setSelectedVariants] = useState<Set<string>>(
    () => new Set(snapshot?.selectedVariants),
  );
  const [downsamplingEnabled, setDownsamplingEnabled] = useState(
    snapshot?.downsamplingEnabled ?? true,
  );

  const {
    samples,
    loading,
    error,
    refetch,
    lastFetchMs,
    loadedFromMs,
    loadedToMs,
  } = useMetrics({ fromMs, toMs, liveTail, snapshot });
  const { registerPods, zoomBus, hiddenPods } = useChartContext();

  useEffect(() => {
    onActionsChange?.({ loading, refresh: refetch });
  }, [loading, onActionsChange, refetch]);
  useEffect(() => () => onActionsChange?.(null), [onActionsChange]);

  useEffect(() => {
    if (loading || lastFetchMs === null || loadedFromMs === null || loadedToMs === null) {
      onSnapshotChange?.(null);
      return;
    }
    onSnapshotChange?.({
      samples,
      selectedVariants: Array.from(selectedVariants),
      hiddenPods: Array.from(hiddenPods),
      downsamplingEnabled,
      lastUpdatedMs: lastFetchMs,
      loadedFromMs,
      loadedToMs,
    });
  }, [
    downsamplingEnabled,
    hiddenPods,
    lastFetchMs,
    loadedFromMs,
    loadedToMs,
    loading,
    onSnapshotChange,
    samples,
    selectedVariants,
  ]);
  useEffect(() => () => onSnapshotChange?.(null), [onSnapshotChange]);

  // Keep mounted Plotly charts aligned when the toolbar/reducer changes range.
  useEffect(() => {
    zoomBus.broadcast('__range-controls__', toMs === null
      ? { 'xaxis.autorange': true }
      : { 'xaxis.range[0]': new Date(fromMs), 'xaxis.range[1]': new Date(toMs) },
      false,
    );
  }, [fromMs, toMs, zoomBus]);

  // Register pods so the shared color map and legend stay in sync.
  useEffect(() => {
    if (samples.length === 0) return;
    const pods = Array.from(new Set(samples.map((s) => s.pod)));
    registerPods(pods);
  }, [samples, registerPods]);

  // Discover variants present in current samples.
  const variants = useMemo(() => {
    const set = new Set<string>();
    for (const s of samples) set.add(podVariant(s.pod));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [samples]);

  const filteredSamples = useMemo(() => {
    if (selectedVariants.size === 0) return samples;
    return samples.filter((s) => selectedVariants.has(podVariant(s.pod)));
  }, [samples, selectedVariants]);
  const displaySamples = useMemo(
    () => downsamplingEnabled ? downsampleMetricsForDisplay(filteredSamples) : filteredSamples,
    [downsamplingEnabled, filteredSamples],
  );

  const toggleVariant = (v: string) => {
    setSelectedVariants((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  };
  const selectAllVariants = () => setSelectedVariants(new Set());

  const setLiveTail = useCallback(
    (value: boolean) => dispatchRange({ type: 'setLiveTail', value }),
    [dispatchRange],
  );

  const onUndo = useCallback(() => dispatchRange({ type: 'undo' }), [dispatchRange]);
  const onRedo = useCallback(() => dispatchRange({ type: 'redo' }), [dispatchRange]);
  const onSetFromTo = useCallback(
    (f: number, t: number | null) => dispatchRange({ type: 'setFromTo', fromMs: f, toMs: t }),
    [dispatchRange],
  );
  const onSetPreset = useCallback(
    (key: TimeRangeKey) => dispatchRange({ type: 'setPreset', key }),
    [dispatchRange],
  );

  // Header subtitle stats.
  const numPods = useMemo(() => new Set(filteredSamples.map((s) => s.pod)).size, [filteredSamples]);
  const ts = lastFetchMs ? new Date(lastFetchMs).toLocaleTimeString() : '—';
  const rangeLabel = presetLabel ?? `${new Date(fromMs).toLocaleTimeString()} – ${toMs ? new Date(toMs).toLocaleTimeString() : 'live'}`;
  const subtitle = `Updated ${ts} · ${rangeLabel} · ${numPods} pods · ${filteredSamples.length.toLocaleString()} data points`;
  const toDisplayMs = toMs ?? lastFetchMs ?? fromMs;

  return (
    <main className={`flex-1 overflow-auto px-4 pb-12 ${showHeader ? 'pt-4' : 'pt-1'}`}>
      <div className="mb-3 min-w-0">
        {showHeader && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-xl font-bold">Adaptive Rate Limiter</h1>
            <MetricsTimeRangeControls
              fromMs={fromMs}
              toMs={toDisplayMs}
              onSetFromTo={onSetFromTo}
              onSetPreset={onSetPreset}
              activePresetLabel={presetLabel}
              toDisabled={liveTail}
              rangeActions={(
                <>
                  <button
                    type="button"
                    disabled={!canUndo}
                    onClick={onUndo}
                    title="Undo zoom"
                    className="h-7 rounded px-2 text-sm hover:bg-muted disabled:opacity-40"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    disabled={!canRedo}
                    onClick={onRedo}
                    title="Redo zoom"
                    className="h-7 rounded px-2 text-sm hover:bg-muted disabled:opacity-40"
                  >
                    →
                  </button>
                </>
              )}
            >
              <PasteGrafanaRangeButton
                onApply={(nextFromMs, nextToMs) => onSetFromTo(nextFromMs, nextToMs)}
                setLiveTail={setLiveTail}
              />
              <MetricsRefreshControls
                label="Live tail"
                checked={liveTail}
                onCheckedChange={setLiveTail}
                loading={loading}
                onRefresh={refetch}
              />
            </MetricsTimeRangeControls>
          </div>
        )}
        <div className={`flex flex-wrap items-center gap-2 ${showHeader ? 'mt-1' : ''}`}>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
          {snapshot === null
            ? <MetricsStorageUsage kind="rate-limit" />
            : <span className="rounded-md border px-1.5 py-1 text-[11px] text-muted-foreground">Snapshot data</span>}
        </div>
      </div>

      <section
        aria-label="Rate limit metrics"
        className="space-y-2 rounded-xl border border-primary/20 bg-muted/40 p-2.5 shadow-sm"
      >
        <FloatingToolbar
          variants={variants}
          selectedVariants={selectedVariants}
          toggleVariant={toggleVariant}
          selectAllVariants={selectAllVariants}
          downsamplingEnabled={downsamplingEnabled}
          toggleDownsampling={() => setDownsamplingEnabled((enabled) => !enabled)}
        />

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {!loading && filteredSamples.length === 0 && (
          <div className="rounded-md border border-muted bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
            No data in the selected window. Try a wider range.
          </div>
        )}

        <div className="grid min-w-0 grid-cols-1 items-start gap-2 xl:grid-cols-2">
          <ChartCard title="Agreed Limit" scope="global" subtitle="req/s · min of all pods via Redis">
            <AgreedLimitChart samples={displaySamples} loading={loading} />
          </ChartCard>

          <ChartCard title="Latency" scope="pods" subtitle="ms">
            <LatencyThresholdLegend />
            <LatencyChart samples={displaySamples} loading={loading} />
          </ChartCard>

          <ChartCard title="Total Requests" scope="global" subtitle="req/s">
            <TotalRequestsChart samples={displaySamples} loading={loading} />
          </ChartCard>

          <ChartCard title="Inflight" scope="pods" subtitle="concurrent">
            <MetricsChart samples={displaySamples} selectField={(s) => s.inflight} yLabel="concurrent" loading={loading} />
          </ChartCard>

          <ChartCard title="Infrastructure Probes" scope="global">
            <InfraProbesChart samples={displaySamples} loading={loading} />
          </ChartCard>

          <ChartCard title="Server CPU" scope="pods" subtitle="%">
            <MetricsChart samples={displaySamples} selectField={(s) => s.server_cpu} yLabel="%" yRange={[0, 110]} loading={loading} />
          </ChartCard>

          <ChartCard title="Server Memory" scope="pods" subtitle="%">
            <MetricsChart samples={displaySamples} selectField={(s) => s.server_memory} yLabel="%" yRange={[0, 110]} loading={loading} />
          </ChartCard>

          <ChartCard title="Calculated Limit" scope="pods" subtitle="req/s" defaultOpen={false}>
            <MetricsChart
              samples={displaySamples}
              selectField={(s) => s.limit_calculated}
              yLabel="req/s"
              yRange={[0, 110]}
              legendSortDirection="asc"
              loading={loading}
            />
          </ChartCard>
        </div>
      </section>
    </main>
  );
}

function PageBody() {
  const [rangeState, dispatchRange] = useReducer(rangeReducer, null, () => makeInitialRangeState(Date.now()));
  const { fromMs, toMs, liveTail, presetLabel, history, historyIndex } = rangeState;

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const handleZoom = useCallback((zFromMs: number, zToMs: number) => {
    dispatchRange({ type: 'zoom', fromMs: zFromMs, toMs: zToMs });
  }, [dispatchRange]);

  return (
    <ChartProvider onZoomRange={handleZoom}>
      <RateLimitMetricsContent
        fromMs={fromMs}
        toMs={toMs}
        liveTail={liveTail}
        presetLabel={presetLabel}
        canUndo={canUndo}
        canRedo={canRedo}
        dispatchRange={dispatchRange}
      />
    </ChartProvider>
  );
}

export default function RateLimitMetricsPage() {
  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <PageBody />
      </div>
    </div>
  );
}
